import { Link, Outlet, useNavigate } from 'react-router';
import { Bouton } from '../shared/ui/Bouton';
import { LienDeNavigation } from '../shared/ui/LienDeNavigation';
import { PiedDePage } from '../shared/ui/PiedDePage';
import {
  ADRESSE_DE_LA_CONNEXION,
  LES_HUIT_PAGES,
} from '../shared/les-huit-pages';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LE CADRE DES SEPT PAGES QUI PRÉSENTENT — MENU EN HAUT, PIED EN BAS.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **C'est une route de mise en page, et non un composant que chaque page appellerait**, pour
 * la raison que `web/` a déjà écrite : « une page qui doit se souvenir de porter le cadre est
 * une page qui l'oubliera, et le jour où elle l'oublie, l'écran perd sa navigation sans que
 * rien ne rougisse ».
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LA PAGE DE CONNEXION N'EST PAS DANS CE CADRE, ET LA PAGE DE REFUS NON PLUS.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * F-71 : la page de connexion « **porte le pied de page et pas le menu.** Ni carrousel, ni
 * bloc tiers. » Elle a donc besoin de **la moitié** de ce cadre, jamais de l'ensemble — et
 * c'est pourquoi le pied est exporté à part. Un cadre commun aux huit avec une condition
 * « sauf la connexion » aurait mis, dans le cadre, une règle qui appartient à la page.
 *
 * **Et le motif de l'écart est plus fort qu'une question de goût** : sur cette page, le menu
 * offrirait sept sorties à quelqu'un venu se connecter, dont l'une le ramènerait ici depuis
 * `web/` ou `admin/` en boucle. La page de refus, elle, « **renvoie à l'accueil** » (F-69) et
 * rien d'autre : un menu complet en ferait une page de navigation, donc une neuvième surface.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LE MENU ET LE PIED SE DÉRIVENT DE LA LISTE FERMÉE, ILS NE LA RECOPIENT PAS.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Voir `shared/les-huit-pages.ts` : les adresses vivent **une fois**, et ce fichier les
 * filtre. Une page ajoutée au routeur sans entrée de menu se verrait donc dans la liste, et
 * non dans trois endroits à comparer à la main.
 */

export function CadreDuSite() {
  const menu = LES_HUIT_PAGES.filter((page) => page.menu !== undefined);
  const naviguer = useNavigate();

  return (
    <div className="flex min-h-dvh flex-col bg-fond-travail">
      {/* **`flex-wrap` et non un menu qui se replie derrière un bouton.** F-73 : « les
          sept pages qui présentent **ne refusent rien et s'adaptent à tout** ». Six entrées
          qui reviennent à la ligne s'adaptent à un téléphone sans qu'aucun état, aucun
          panneau et aucune animation n'existent — et un menu qui se déploie est un état de
          plus sur une surface qui n'en a aucun. */}
      <header className="border-b border-bord bg-fond-surface px-6 py-3">
        <div className="mx-auto flex max-w-espace flex-wrap items-center justify-between gap-4">
          <Link
            to="/"
            className="text-18 font-weight-800 tracking-marque text-texte-fort"
          >
            SWYPE
          </Link>

          <nav className="flex flex-wrap items-center gap-1">
            {menu.map((page) => (
              <LienDeNavigation key={page.adresse} vers={page.adresse}>
                {page.menu}
              </LienDeNavigation>
            ))}
          </nav>

          {/* ═══════════════════════════════════════════════════════════════
              **LE CHEMIN VERS LA CONNEXION DIT TOUJOURS « SE CONNECTER ».**
              ═══════════════════════════════════════════════════════════════

              F-71 : « **les sept autres pages ne savent rien de personne** : elles montrent
              la même chose à tout le monde, et **le chemin dit toujours "se connecter"**. »

              **C'est une règle et non un raccourci d'implémentation.** Un menu qui dirait
              « accéder à mon espace » à qui a une session vivante **aurait lu la session**,
              donc ces sept pages sauraient quelque chose de quelqu'un — et une page publique
              qui varie selon le visiteur ne peut plus être servie identiquement à tous, ce
              que l'indexation suppose (F-73 : « le site public est indexable »).

              **La session ne se lit donc QUE sur la page 6**, où F-71 l'exige : « elle
              reconnaît une session vivante et emmène la personne dans son espace ». Le
              partage est net — sept pages aveugles, une page qui regarde.

              **Il est rendu comme une ACTION et non comme un onglet**, alors que la page 6
              est bien l'une des huit : un onglet paraîtrait actif sur `/connexion`, page qui
              n'a précisément pas ce menu, et le libellé « se connecter » est un geste, pas
              une destination.

              **Mais c'est bien une navigation interne**, donc `useNavigate` et non
              `window.location` : la page 6 est servie par la même application, et un
              rechargement complet retéléchargerait le paquet pour rien. */}
          <Bouton
            variante="action"
            taille="petite"
            onClick={() => {
              // `useNavigate` rend une promesse en React Router 7, et rien n'attend son
              // aboutissement : le `void` le dit au lieu de le laisser deviner.
              void naviguer(ADRESSE_DE_LA_CONNEXION);
            }}
          >
            Se connecter
          </Bouton>
        </div>
      </header>

      {/* `flex-1` : le pied reste en bas même sur une page courte, et c'est le seul motif
          de cette classe. Une page de texte court sans cela laisserait le pied au milieu. */}
      <main className="flex-1">
        <Outlet />
      </main>

      <PiedDePage />
    </div>
  );
}
